import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Plus, 
  Trash2, 
  Save, 
  Image as ImageIcon, 
  Scale, 
  Sparkles, 
  ArrowUpDown, 
  Upload, 
  Check, 
  AlertCircle,
  FolderPlus,
  HeartPulse,
  ArrowRight,
  Package,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { 
  generateStandardVariants,
  generateFourStandardVariants, 
  calculatePriceForGrams, 
  getMarkupPercentage,
  sortVariantsByWeight 
} from '../../utils/weightPricing';
import { generateDefaultProductAltText } from '../../utils/seoHelpers';

const ProductFormModal = ({ isOpen, onClose, onSave, editingProduct }) => {
  const { lang, t } = useLanguage();
  const { categories, mizajList, ailments, addCategory, addAilment, deleteImageFromServer } = useStore();

  const [kgRateInput, setKgRateInput] = useState('1200');
  const [imageMode, setImageMode] = useState('upload'); // 'upload' or 'url'
  const [urlInput, setUrlInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Quick Add Category State
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCatData, setNewCatData] = useState({
    nameUrdu: '',
    nameEn: '',
    taglineUrdu: '',
    icon: 'Leaf'
  });

  // Quick Add Ailment / Disease Filter State
  const [showNewAilmentForm, setShowNewAilmentForm] = useState(false);
  const [newAilmentData, setNewAilmentData] = useState({
    nameUrdu: '',
    nameEn: ''
  });

  const [formData, setFormData] = useState({
    nameUrdu: '',
    nameEn: '',
    category: 'raw-herbs',
    image: '',
    images: [],
    imageAlt: '',
    mizaj: '',
    mizajUrdu: '',
    mizajEn: '',
    benefitsUrdu: '',
    benefitsEn: '',
    usageUrdu: '',
    usageEn: '',
    precautionsUrdu: '',
    precautionsEn: '',
    inStock: true,
    stockCount: 50,
    featured: false,
    variants: generateStandardVariants(1200)
  });

  useEffect(() => {
    if (editingProduct) {
      // Sort existing product variants ascending by weight
      const sortedExisting = sortVariantsByWeight(editingProduct.variants || []);
      const loadedImages = Array.isArray(editingProduct.images) && editingProduct.images.length > 0
        ? editingProduct.images
        : (editingProduct.image ? [editingProduct.image] : []);

      setFormData({
        ...editingProduct,
        image: loadedImages[0] || editingProduct.image || '',
        images: loadedImages,
        imageAlt: editingProduct.imageAlt || editingProduct.altText || '',
        ailments: editingProduct.ailments || [],
        variants: sortedExisting
      });
      // Try to find a 1kg variant to prefill the kg rate
      const kgVar = sortedExisting.find(v => 
        (v.weight && (v.weight.includes('کلو') || v.weight.includes('1000'))) ||
        (v.weightEn && v.weightEn.toLowerCase().includes('1 kg'))
      );
      if (kgVar && kgVar.price) {
        setKgRateInput(String(kgVar.price));
      } else if (sortedExisting.length > 0) {
        setKgRateInput('');
      }
    } else {
      setFormData({
        nameUrdu: '',
        nameEn: '',
        slug: '',
        category: categories?.[0]?.id || 'raw-herbs',
        image: '',
        images: [],
        imageAlt: '',
        mizaj: '',
        mizajUrdu: '',
        mizajEn: '',
        benefitsUrdu: '',
        benefitsEn: '',
        usageUrdu: '',
        usageEn: '',
        precautionsUrdu: '',
        precautionsEn: '',
        inStock: true,
        stockCount: 50,
        featured: false,
        ailments: [],
        variants: generateStandardVariants(1200)
      });
      setKgRateInput('1200');
    }
    setUrlInput('');
    setShowNewCategoryForm(false);
    setShowNewAilmentForm(false);
  }, [editingProduct, isOpen, categories]);

  // Quick Category Creation Handler
  const handleQuickCreateCategory = () => {
    if (!newCatData.nameUrdu.trim()) return;
    const cleanSlug = newCatData.nameEn?.trim()
      ? newCatData.nameEn.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-')
      : `cat-${Date.now()}`;
    const newCat = addCategory({
      id: cleanSlug,
      nameUrdu: newCatData.nameUrdu.trim(),
      nameEn: newCatData.nameEn.trim() || newCatData.nameUrdu.trim(),
      taglineUrdu: newCatData.taglineUrdu.trim() || 'اعلیٰ معیار کی جڑی بوٹیاں و دیسی ادویات',
      taglineEn: newCatData.nameEn.trim() || '',
      icon: newCatData.icon || 'Leaf',
      bgGradient: 'from-emerald-800 to-emerald-950',
      accentColor: 'text-emerald-400',
      itemCount: '1+ اشیاء'
    });

    setFormData(prev => ({ ...prev, category: newCat.id }));
    setShowNewCategoryForm(false);
    setNewCatData({ nameUrdu: '', nameEn: '', taglineUrdu: '', icon: 'Leaf' });
  };

  // Quick Ailment Filter Creation Handler
  const handleQuickCreateAilment = () => {
    if (!newAilmentData.nameUrdu.trim()) return;
    const cleanSlug = newAilmentData.nameEn?.trim()
      ? newAilmentData.nameEn.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-')
      : `ailment-${Date.now()}`;
    const newAil = addAilment({
      id: cleanSlug,
      nameUrdu: newAilmentData.nameUrdu.trim(),
      nameEn: newAilmentData.nameEn.trim() || newAilmentData.nameUrdu.trim()
    });

    setFormData(prev => {
      const currentAilments = prev.ailments || [];
      return {
        ...prev,
        ailments: currentAilments.includes(newAil.id) ? currentAilments : [...currentAilments, newAil.id]
      };
    });
    setShowNewAilmentForm(false);
    setNewAilmentData({ nameUrdu: '', nameEn: '' });
  };

  const compressImageFile = (file) => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        return reject(new Error('صرف تصویر فائل سپورٹڈ ہے'));
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxW = 900;
          let w = img.width;
          let h = img.height;
          if (w > maxW) {
            h = Math.round((h * maxW) / w);
            w = maxW;
          }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const compressed = canvas.toDataURL('image/webp', 0.85);
          resolve(compressed);
        };
        img.onerror = () => reject(new Error('تصویر لوڈ کرنے میں مسئلہ'));
        img.src = event.target.result;
      };
      reader.onerror = () => reject(new Error('فائل پڑھنے میں مسئلہ'));
      reader.readAsDataURL(file);
    });
  };

  const uploadImageToServer = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadUrl = window.location.pathname.includes('/himalayan')
        ? '/himalayan/api/upload.php'
        : 'api/upload.php';
      
      const res = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        if (data && data.url) {
          const finalUrl = window.location.pathname.includes('/himalayan') && !data.url.startsWith('/himalayan')
            ? `/himalayan/${data.url.replace(/^\//, '')}`
            : data.url;
          return finalUrl;
        }
      }
    } catch (serverErr) {
      console.warn('Direct upload error, falling back to compression:', serverErr);
    }
    // Fallback if server upload is not reachable (e.g. offline dev)
    return await compressImageFile(file);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        if (file.type.startsWith('image/')) {
          const url = await uploadImageToServer(file);
          if (url) uploadedUrls.push(url);
        }
      }

      if (uploadedUrls.length > 0) {
        setFormData(prev => {
          const updatedImages = [...(prev.images || []), ...uploadedUrls];
          return {
            ...prev,
            images: updatedImages,
            image: updatedImages[0] || ''
          };
        });
      }
    } catch (err) {
      alert('تصویر اپلوڈ کرنے میں مسئلہ پیش آیا: ' + err.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddImageUrl = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    setFormData(prev => {
      const updatedImages = [...(prev.images || []), trimmed];
      return {
        ...prev,
        images: updatedImages,
        image: updatedImages[0] || ''
      };
    });
    setUrlInput('');
  };

  const handleRemoveImage = (indexToRemove) => {
    const imgToRemove = (formData.images || [])[indexToRemove];
    if (imgToRemove && imgToRemove.includes('uploads/') && deleteImageFromServer) {
      deleteImageFromServer(imgToRemove);
    }
    setFormData(prev => {
      const updatedImages = (prev.images || []).filter((_, i) => i !== indexToRemove);
      return {
        ...prev,
        images: updatedImages,
        image: updatedImages[0] || ''
      };
    });
  };

  const handleSetPrimaryImage = (indexToPrimary) => {
    setFormData(prev => {
      const current = [...(prev.images || [])];
      if (indexToPrimary <= 0 || indexToPrimary >= current.length) return prev;
      const [item] = current.splice(indexToPrimary, 1);
      current.unshift(item);
      return {
        ...prev,
        images: current,
        image: current[0] || ''
      };
    });
  };

  const handleVariantChange = (index, field, value) => {
    const updated = [...formData.variants];
    updated[index][field] = field === 'price' ? Number(value) : value;
    setFormData({ ...formData, variants: updated });
  };

  const handleApplyFourWeights = () => {
    const rate = Number(kgRateInput);
    if (!rate || rate <= 0) return;
    const standardVariants = generateStandardVariants(rate);
    setFormData({
      ...formData,
      variants: sortVariantsByWeight(standardVariants)
    });
  };

  const handleAddSpecificWeight = (grams, labelUrdu, labelEn) => {
    const rate = Number(kgRateInput);
    if (!rate || rate <= 0) return;
    const price = calculatePriceForGrams(grams, rate);
    
    // Check if variant with this label or grams already exists
    const existingIndex = formData.variants.findIndex(v => 
      v.weight === labelUrdu || v.grams === grams
    );
    let updated;
    if (existingIndex >= 0) {
      updated = [...formData.variants];
      updated[existingIndex] = { weight: labelUrdu, weightEn: labelEn || `${grams}g`, grams, price };
    } else {
      updated = [
        ...formData.variants,
        { weight: labelUrdu, weightEn: labelEn || `${grams}g`, grams, price }
      ];
    }
    // Auto-sort ascending by weight (so 50g appears before 100g, 750g before 1kg)
    setFormData({
      ...formData,
      variants: sortVariantsByWeight(updated)
    });
  };

  const addVariant = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        { weight: '250 گرام', weightEn: '250g', price: 300 }
      ]
    });
  };

  const handleManualSort = () => {
    setFormData({
      ...formData,
      variants: sortVariantsByWeight(formData.variants)
    });
  };

  const removeVariant = (index) => {
    if (formData.variants.length <= 1) return;
    setFormData({
      ...formData,
      variants: formData.variants.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.nameUrdu.trim()) return;
    
    // Generate URL Slug if missing
    let finalSlug = formData.slug?.trim();
    if (!finalSlug) {
      finalSlug = formData.nameEn?.toLowerCase().trim().replace(/[^a-z0-9-]+/g, '-') || formData.nameUrdu?.trim().replace(/[^a-z0-9\u0600-\u06FF-]+/g, '-');
    }
    if (!finalSlug) {
      finalSlug = `prod-${Date.now()}`;
    }

    // Auto-sort variants ascending by weight on save
    const sorted = sortVariantsByWeight(formData.variants);
    const cleanedImages = (formData.images || []).filter(Boolean);
    const primaryImg = cleanedImages[0] || formData.image || '';
    onSave({
      ...formData,
      slug: finalSlug,
      id: formData.id || finalSlug, // If it's a new product, use slug as ID as well for clean URLs, or just keep it separate
      imageAlt: (formData.imageAlt || '').trim(),
      image: primaryImg,
      images: cleanedImages.length > 0 ? cleanedImages : (primaryImg ? [primaryImg] : []),
      variants: sorted
    });
    onClose();
  };

  const autoGeneratedAlt = generateDefaultProductAltText(formData, 0, 'ur');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-50 flex flex-col animate-fade-in text-right" dir="rtl">
      {/* Sticky Top Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-200 px-4 sm:px-8 py-3.5 shadow-xs">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 font-urdu font-bold text-xs sm:text-sm transition cursor-pointer"
            >
              <ArrowRight className="w-4 h-4" />
              <span>واپس پروڈکٹس لسٹ پر جائیں</span>
            </button>
            <div className="hidden sm:block h-6 w-px bg-gray-200" />
            <div>
              <h1 className="font-bold text-base sm:text-lg font-urdu text-pansar-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-pansar-700" />
                <span>{editingProduct ? `پروڈکٹ میں ترمیم: ${formData.nameUrdu || editingProduct.nameUrdu}` : 'نئی پروڈکٹ / دوا کا اندراج'}</span>
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 text-xs sm:text-sm font-bold font-urdu transition cursor-pointer"
            >
              منسوخ کریں
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="px-5 py-2 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white text-xs sm:text-sm font-bold font-urdu flex items-center gap-1.5 shadow-sm transition cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>محفوظ کریں (Save)</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Full-Page Content Form */}
      <div className="flex-1 max-w-6xl mx-auto w-full p-4 sm:p-8 space-y-6 pb-24">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Card 1: Basic Information */}
          <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-5">
            <div className="border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-pansar-900 font-urdu flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-pansar-600"></span>
                بنیادی معلومات و کیٹیگری (Basic Information)
              </h2>
            </div>

            {/* Titles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  دوا / پروڈکٹ کا اردو نام *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nameUrdu}
                onChange={(e) => setFormData({ ...formData, nameUrdu: e.target.value })}
                placeholder="مثلاً: کلونجی خالص اول درجہ"
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 font-sans mb-1">
                English Name (انگریزی نام)
              </label>
              <input
                type="text"
                value={formData.nameEn}
                onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                placeholder="e.g. Pure Kalonji Seeds"
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans focus:border-pansar-700 focus:outline-none text-left dir-ltr"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-gray-700 font-urdu">
                کیٹیگری (قسم دوا) *
              </label>
              <button
                type="button"
                onClick={() => setShowNewCategoryForm(!showNewCategoryForm)}
                className="text-xs text-emerald-700 hover:text-emerald-900 font-bold font-urdu flex items-center gap-1 hover:underline cursor-pointer bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200 transition"
              >
                <FolderPlus className="w-3.5 h-3.5" />
                <span>+ نئی کیٹیگری بنائیں</span>
              </button>
            </div>

            {/* Quick New Category Form Box */}
            {showNewCategoryForm && (
              <div className="p-3.5 bg-emerald-50/80 border border-emerald-200 rounded-2xl space-y-3 mb-3 animate-fade-in shadow-xs">
                <div className="flex items-center justify-between border-b border-emerald-200/80 pb-1.5">
                  <span className="text-xs font-bold text-emerald-950 font-urdu flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
                    نئی کیٹیگری کا فوری اندراج
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowNewCategoryForm(false)}
                    className="text-gray-400 hover:text-gray-600 text-xs px-1.5 py-0.5 rounded cursor-pointer"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] font-bold text-gray-700 font-urdu block mb-1">
                      کیٹیگری کا اردو نام *
                    </label>
                    <input
                      type="text"
                      value={newCatData.nameUrdu}
                      onChange={(e) => setNewCatData({ ...newCatData, nameUrdu: e.target.value })}
                      placeholder="مثلاً: کشتہ جات اور جوہرات"
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-urdu focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-700 font-sans block mb-1">
                      English Name (اختیاری)
                    </label>
                    <input
                      type="text"
                      value={newCatData.nameEn}
                      onChange={(e) => setNewCatData({ ...newCatData, nameEn: e.target.value })}
                      placeholder="e.g. Kushtajat & Minerals"
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-sans text-left dir-ltr focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] font-bold text-gray-700 font-urdu block mb-1">
                      مختصر تعارف / ٹیگ لائن (اختیاری)
                    </label>
                    <input
                      type="text"
                      value={newCatData.taglineUrdu}
                      onChange={(e) => setNewCatData({ ...newCatData, taglineUrdu: e.target.value })}
                      placeholder="مثلاً: قانون مفرد اعضاء کے معیاری کشتہ جات"
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-urdu focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-700 font-urdu block mb-1">
                      آئیکن (Icon)
                    </label>
                    <select
                      value={newCatData.icon}
                      onChange={(e) => setNewCatData({ ...newCatData, icon: e.target.value })}
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-sans focus:outline-none focus:border-emerald-600"
                    >
                      <option value="Leaf">Leaf (جڑی بوٹی / پتا)</option>
                      <option value="Droplets">Droplets (تیل / عرق)</option>
                      <option value="Sparkles">Sparkles (معجون / خاص)</option>
                      <option value="ShieldCheck">ShieldCheck (کشتہ / حفاظت)</option>
                      <option value="Package">Package (ڈبہ / پیکٹ)</option>
                      <option value="Flame">Flame (حرارت / مقوی)</option>
                      <option value="Heart">Heart (دل / صحت)</option>
                      <option value="Stethoscope">Stethoscope (طبی علاج)</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end items-center gap-2 pt-1 border-t border-emerald-200/50">
                  <button
                    type="button"
                    onClick={() => setShowNewCategoryForm(false)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-urdu hover:bg-gray-100 cursor-pointer"
                  >
                    منسوخ
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickCreateCategory}
                    disabled={!newCatData.nameUrdu.trim()}
                    className="px-4 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-xs font-bold font-urdu transition cursor-pointer flex items-center gap-1 shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>کیٹیگری محفوظ اور منتخب کریں</span>
                  </button>
                </div>
              </div>
            )}

            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none bg-white"
            >
              {(categories || []).map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.nameUrdu} ({cat.nameEn})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Card 2: Product Multi-Image Gallery Manager */}
          <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-5">
            <div className="border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-pansar-900 font-urdu flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-pansar-600"></span>
                پروڈکٹ کی تصاویر و گیلری (Multi-Image Gallery)
              </h2>
            </div>

            <div className="bg-gradient-to-b from-gray-50 to-emerald-50/20 rounded-2xl p-4 border border-emerald-100/80 space-y-3.5 shadow-2xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-pansar-800 text-white flex items-center justify-center">
                  <ImageIcon className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-xs font-bold text-gray-900 font-urdu block">
                    پروڈکٹ کی تصاویر و گیلری (Multi-Image Gallery)
                  </span>
                  <span className="text-[10px] text-gray-500 font-urdu">
                    ایک پروڈکٹ کے لیے متعدد تصاویر شامل کریں (پہلی تصویر مرکزی کور ہوگی)
                  </span>
                </div>
              </div>

              {/* Mode Toggle */}
              <div className="flex bg-white rounded-xl p-0.5 border border-gray-200 text-[11px] font-urdu shadow-2xs">
                <button
                  type="button"
                  onClick={() => setImageMode('upload')}
                  className={`px-3 py-1 rounded-lg transition font-bold cursor-pointer ${
                    imageMode === 'upload' 
                      ? 'bg-pansar-800 text-white shadow-xs' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  موبائل / کمپیوٹر فائلز
                </button>
                <button
                  type="button"
                  onClick={() => setImageMode('url')}
                  className={`px-3 py-1 rounded-lg transition font-bold cursor-pointer ${
                    imageMode === 'url' 
                      ? 'bg-pansar-800 text-white shadow-xs' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  انٹرنیٹ لنک (URL)
                </button>
              </div>
            </div>

            {/* Upload or URL Controls */}
            {imageMode === 'upload' ? (
              <div className="space-y-1.5">
                <button
                  type="button"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3 px-4 rounded-xl border-2 border-dashed border-emerald-300 hover:border-emerald-600 bg-white hover:bg-emerald-50/50 text-pansar-900 font-bold text-xs font-urdu transition flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                >
                  <Upload className="w-4 h-4 text-emerald-700" />
                  <span>
                    {isUploading ? 'تصاویر ایڈجسٹ ہو رہی ہیں...' : '+ اپنے کمپیوٹر یا موبائل سے ایک یا ایک سے زائد تصاویر منتخب کریں'}
                  </span>
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <span className="text-[10px] text-gray-500 font-urdu block">
                  💡 آپ ایک ساتھ ایک سے زیادہ تصاویر (JPG, PNG, WebP) منتخب کر سکتے ہیں۔ سسٹم خودکار طور پر ریزولوشن تیز اور محفوظ کر لے گا۔
                </span>
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddImageUrl();
                      }
                    }}
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 p-2.5 rounded-xl border border-gray-200 text-xs font-sans focus:border-pansar-700 focus:outline-none text-left dir-ltr bg-white"
                  />
                  <button
                    type="button"
                    onClick={handleAddImageUrl}
                    disabled={!urlInput.trim()}
                    className="px-4 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white text-xs font-bold font-urdu flex items-center gap-1 shadow-xs cursor-pointer transition"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>شامل کریں</span>
                  </button>
                </div>
                <span className="text-[10px] text-gray-500 font-urdu block">
                  کسی بھی ویب لنک کو پیسٹ کر کے "شامل کریں" پر کلک کریں۔
                </span>
              </div>
            )}

            {/* Gallery Thumbnails List */}
            {Array.isArray(formData.images) && formData.images.length > 0 ? (
              <div className="space-y-2 pt-2 border-t border-emerald-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-800 font-urdu">
                    منتخب شدہ تصاویر ({formData.images.length})
                  </span>
                  <span className="text-[10px] text-emerald-800 font-urdu bg-emerald-100/70 px-2 py-0.5 rounded-md font-semibold">
                    ⭐ پہلی تصویر سٹور پر بنیادی کور ہوگی
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {formData.images.map((imgSrc, index) => {
                    const isPrimary = index === 0;
                    return (
                      <div 
                        key={index}
                        className={`group relative rounded-xl overflow-hidden bg-white border-2 transition shadow-xs ${
                          isPrimary 
                            ? 'border-amber-500 ring-2 ring-amber-400/40' 
                            : 'border-gray-200 hover:border-pansar-400'
                        }`}
                      >
                        <div className="aspect-square w-full overflow-hidden bg-gray-100 flex items-center justify-center">
                          <img 
                            src={imgSrc} 
                            alt={`Product image ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                          />
                        </div>

                        {/* Top Badges & Actions */}
                        <div className="absolute top-1.5 inset-x-1.5 flex items-center justify-between gap-1 pointer-events-none">
                          {isPrimary ? (
                            <span className="px-1.5 py-0.5 rounded-md bg-amber-500 text-white text-[9px] font-bold font-urdu shadow">
                              مرکزی
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded-md bg-black/60 text-white text-[9px] font-bold font-sans">
                              #{index + 1}
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="p-1 rounded-full bg-rose-600/90 hover:bg-rose-700 text-white shadow transition cursor-pointer pointer-events-auto"
                            title="یہ تصویر ہٹائیں"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Bottom Actions if not primary */}
                        {!isPrimary && (
                          <div className="p-1 bg-gray-50 border-t border-gray-100 text-center">
                            <button
                              type="button"
                              onClick={() => handleSetPrimaryImage(index)}
                              className="w-full py-1 text-[10px] font-bold text-amber-800 hover:text-amber-950 bg-amber-50 hover:bg-amber-100 rounded border border-amber-200 transition font-urdu cursor-pointer"
                            >
                              ⭐ مرکزی بنائیں
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl border border-dashed border-gray-300 bg-white text-center text-gray-400">
                <ImageIcon className="w-8 h-8 mx-auto mb-1.5 text-gray-300" />
                <span className="text-xs font-urdu font-medium text-gray-600 block">
                  ابھی کوئی تصویر شامل نہیں کی گئی
                </span>
                <span className="text-[10px] font-urdu text-gray-400 block mt-0.5">
                  اوپر دیئے گئے بٹن سے ایک یا ایک سے زیادہ تصاویر شامل کریں
                </span>
              </div>
            )}

            {/* SEO Alt Text Configuration & Live Preview */}
            <div className="mt-5 pt-4 border-t border-emerald-100 space-y-3 bg-gradient-to-br from-emerald-50/50 to-teal-50/30 -mx-5 -mb-5 p-5 rounded-b-3xl border-b border-gray-100">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                  <label className="text-xs font-bold text-gray-900 font-urdu">
                    تصویر کا آلٹ ٹیکسٹ (SEO Alt Text / متبادل متن)
                  </label>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-urdu font-bold text-emerald-800 bg-white px-2.5 py-0.5 rounded-full border border-emerald-200 shadow-2xs">
                  <Sparkles className="w-3 h-3 text-emerald-600" />
                  <span>گوگل امیج سرچ (Google SEO)</span>
                </span>
              </div>

              {/* Auto-generated Preview Box */}
              <div className="bg-white border border-emerald-200/90 rounded-2xl p-3.5 space-y-2 shadow-2xs">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[11px] font-bold text-emerald-950 font-urdu flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                    <span>سسٹم کا خودکار تجویز کردہ آلٹ ٹیکسٹ (Auto-Generated):</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, imageAlt: autoGeneratedAlt }))}
                    className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 font-urdu hover:underline cursor-pointer flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100/80 px-2.5 py-1 rounded-lg border border-emerald-200 transition"
                    title="خودکار ٹیکسٹ کو نیچے کسٹم فیلڈ میں منتقل کریں تاکہ آپ اس میں ردوبدل کر سکیں"
                  >
                    <span>یہ ٹیکسٹ فیلڈ میں نقل کریں ⬇</span>
                  </button>
                </div>
                <div className="p-2.5 bg-emerald-50/60 rounded-xl border border-emerald-100/80 text-xs font-urdu text-emerald-950 font-medium leading-relaxed select-all">
                  {autoGeneratedAlt}
                </div>
              </div>

              {/* Custom Input Field */}
              <div className="space-y-1.5 pt-1">
                <label className="block text-[11px] font-bold text-gray-700 font-urdu">
                  مخصوص آلٹ ٹیکسٹ درج کریں (اگر آپ خود تبدیل کرنا چاہیں):
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.imageAlt || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, imageAlt: e.target.value }))}
                    placeholder="اگر خالی چھوڑیں گے تو اوپر دیا گیا خودکار آلٹ ٹیکسٹ استعمال ہوگا..."
                    className="w-full p-2.5 pr-3 pl-24 rounded-xl border border-gray-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none text-xs font-urdu bg-white shadow-2xs"
                  />
                  {formData.imageAlt && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, imageAlt: '' }))}
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-600 hover:text-rose-700 font-urdu cursor-pointer bg-gray-100 hover:bg-rose-50 px-2.5 py-1 rounded-lg border border-gray-200 transition font-bold"
                    >
                      خالی کریں (آٹو پر رکھیں)
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between text-[10px] text-gray-500 font-urdu pt-0.5">
                  <span>💡 گوگل سرچ کے لیے بہترین آلٹ ٹیکسٹ 100 سے 150 حروف پر مشتمل ہوتا ہے۔</span>
                  {formData.imageAlt && (
                    <span className="font-sans font-bold text-emerald-700 bg-white px-2 py-0.5 rounded border border-emerald-100">
                      {formData.imageAlt.length} حروف
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: Herbal & Medical Details */}
          <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-5">
            <div className="border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-pansar-900 font-urdu flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-pansar-600"></span>
                طبی مزاج، فوائد اور متعلقہ امراض (Medical & Botanical Details)
              </h2>
            </div>

            {/* Mizaj (Temperament) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  مزاج کی درجہ بندی (اختیاری)
                </label>
                <input
                  type="text"
                  value={formData.mizajUrdu || ''}
                onChange={(e) => setFormData({ ...formData, mizajUrdu: e.target.value })}
                placeholder="مثلاً: گرم خشک درجہ دوم"
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                مزاج کا عمومی گروپ (اختیاری)
              </label>
              <select
                value={formData.mizaj || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  const groupObj = (mizajList || []).find(m => m.id === val);
                  setFormData({
                    ...formData,
                    mizaj: val,
                    mizajEn: groupObj?.nameEn || ''
                  });
                }}
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none bg-white"
              >
                <option value="">-- کوئی نہیں (اختیاری) --</option>
                {(mizajList || []).filter(m => m.id !== 'all').map(m => (
                  <option key={m.id} value={m.id}>
                    {m.nameUrdu}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Ailments & Disease Tags Selection */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-gray-700 font-urdu">
                متعلقہ امراض و شکایات برائے فلٹرز (Ailments & Conditions)
              </label>
              <button
                type="button"
                onClick={() => setShowNewAilmentForm(!showNewAilmentForm)}
                className="text-xs text-rose-700 hover:text-rose-900 font-bold font-urdu flex items-center gap-1 hover:underline cursor-pointer bg-rose-50 px-2 py-0.5 rounded-md border border-rose-200 transition"
              >
                <HeartPulse className="w-3.5 h-3.5" />
                <span>+ نئی بیماری / فلٹر بنائیں</span>
              </button>
            </div>

            {/* Quick New Ailment Filter Form Box */}
            {showNewAilmentForm && (
              <div className="p-3.5 bg-rose-50/80 border border-rose-200 rounded-2xl space-y-3 mb-3 animate-fade-in shadow-xs">
                <div className="flex items-center justify-between border-b border-rose-200/80 pb-1.5">
                  <span className="text-xs font-bold text-rose-950 font-urdu flex items-center gap-1.5">
                    <HeartPulse className="w-3.5 h-3.5 text-rose-600" />
                    نئے مرض یا شکایت کے فلٹر کا فوری اندراج
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowNewAilmentForm(false)}
                    className="text-gray-400 hover:text-gray-600 text-xs px-1.5 py-0.5 rounded cursor-pointer"
                  >
                    ✕
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] font-bold text-gray-700 font-urdu block mb-1">
                      بیماری / شکایت کا اردو نام *
                    </label>
                    <input
                      type="text"
                      value={newAilmentData.nameUrdu}
                      onChange={(e) => setNewAilmentData({ ...newAilmentData, nameUrdu: e.target.value })}
                      placeholder="مثلاً: یرقان، ہیپاٹائٹس و جگر کی کمزوری"
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-urdu focus:outline-none focus:border-rose-600"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-gray-700 font-sans block mb-1">
                      English Name (اختیاری)
                    </label>
                    <input
                      type="text"
                      value={newAilmentData.nameEn}
                      onChange={(e) => setNewAilmentData({ ...newAilmentData, nameEn: e.target.value })}
                      placeholder="e.g. Jaundice & Hepatitis"
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-sans text-left dir-ltr focus:outline-none focus:border-rose-600"
                    />
                  </div>
                </div>

                <div className="flex justify-end items-center gap-2 pt-1 border-t border-rose-200/50">
                  <button
                    type="button"
                    onClick={() => setShowNewAilmentForm(false)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-xs font-urdu hover:bg-gray-100 cursor-pointer"
                  >
                    منسوخ
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickCreateAilment}
                    disabled={!newAilmentData.nameUrdu.trim()}
                    className="px-4 py-1.5 rounded-lg bg-rose-700 hover:bg-rose-800 disabled:opacity-50 text-white text-xs font-bold font-urdu transition cursor-pointer flex items-center gap-1 shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>فلٹر محفوظ اور منتخب کریں</span>
                  </button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-2.5 rounded-xl bg-gray-50 border border-gray-200">
              {(ailments || []).map(ail => {
                const isSelected = (formData.ailments || []).includes(ail.id);
                return (
                  <button
                    key={ail.id}
                    type="button"
                    onClick={() => {
                      const current = formData.ailments || [];
                      if (isSelected) {
                        setFormData({ ...formData, ailments: current.filter(id => id !== ail.id) });
                      } else {
                        setFormData({ ...formData, ailments: [...current, ail.id] });
                      }
                    }}
                    className={`px-3 py-1 rounded-lg text-xs font-urdu transition-all border ${
                      isSelected
                        ? 'bg-emerald-700 text-white border-emerald-700 font-bold shadow-xs'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-pansar-400'
                    }`}
                  >
                    {lang === 'ur' ? ail.nameUrdu : ail.nameEn}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Benefits / افعال و خواص */}
          <div>
            <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
              افعال و خواص اور طبی فوائد (Benefits) *
            </label>
            <textarea
              rows="2"
              required
              value={formData.benefitsUrdu}
              onChange={(e) => setFormData({ ...formData, benefitsUrdu: e.target.value })}
              placeholder="دوا کے اہم ترین فوائد اور اثرات..."
              className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none"
            />
          </div>

          {/* Usage & Precautions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                طریقہ استعمال و مقدار خوراک
              </label>
              <input
                type="text"
                value={formData.usageUrdu}
                onChange={(e) => setFormData({ ...formData, usageUrdu: e.target.value })}
                placeholder="مثلاً: ایک چمچ صبح نہار منہ ہمراہ دودھ"
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                احتیاطی تدابیر و پرہیز
              </label>
              <input
                type="text"
                value={formData.precautionsUrdu}
                onChange={(e) => setFormData({ ...formData, precautionsUrdu: e.target.value })}
                placeholder="مثلاً: حاملہ خواتین یا شوگر کے مریض پرہیز کریں"
                className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-urdu focus:border-pansar-700 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Card 4: Variants & Pricing */}
          <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-5">
            <div className="border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-pansar-900 font-urdu flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-pansar-600"></span>
                اوزان، پیکنگ اور قیمتوں کا انتظام (Weights & Pricing)
              </h2>
            </div>

            {/* Auto Weight & Price Calculator Bar for Pansar / Herbs */}
            <div className="bg-gradient-to-r from-emerald-50 to-pansar-50/70 border border-emerald-200 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-emerald-700 text-white flex items-center justify-center">
                    <Scale className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-emerald-950 font-urdu block">
                      کلو کے ریٹ سے خودکار اوزان و قیمتیں (Pansar Auto Pricing Formula)
                    </span>
                    <span className="text-[10px] text-emerald-700 font-urdu">
                      کلو کا ریٹ درج کریں، 4 اوزان بمعہ ریٹیل منافع خودکار بن جائیں گے
                    </span>
                  </div>
                </div>
                <span className="text-[10px] font-bold bg-emerald-100 text-emerald-900 px-2.5 py-1 rounded-full font-urdu border border-emerald-200">
                  ⚡ خودکار کیلکولیٹر
                </span>
              </div>

              {/* Four Tier Formula Indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[10px] font-urdu">
                <div className="bg-white/90 p-1.5 rounded-xl border border-emerald-200/70 text-center text-gray-700 shadow-2xs">
                  سو گرام سے کم: <span className="font-bold text-rose-700">20% اضافہ</span>
                </div>
                <div className="bg-white/90 p-1.5 rounded-xl border border-emerald-200/70 text-center text-gray-700 shadow-2xs">
                  100 تا 250 گرام: <span className="font-bold text-amber-700">15% اضافہ</span>
                </div>
                <div className="bg-white/90 p-1.5 rounded-xl border border-emerald-200/70 text-center text-gray-700 shadow-2xs">
                  250 تا 500 گرام: <span className="font-bold text-pansar-700">10% اضافہ</span>
                </div>
                <div className="bg-white/90 p-1.5 rounded-xl border border-emerald-200/70 text-center text-gray-700 shadow-2xs">
                  500 تا 750 گرام: <span className="font-bold text-emerald-700">5% اضافہ</span>
                </div>
              </div>

              {/* Input 1 Kg Price and Action Button */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                <div className="relative flex-1">
                  <input
                    type="number"
                    value={kgRateInput}
                    onChange={(e) => setKgRateInput(e.target.value)}
                    placeholder="ایک کلو (1000g) کی قیمت روپے میں..."
                    className="w-full pl-3 pr-24 py-2.5 rounded-xl border border-emerald-300 text-xs font-sans text-right focus:outline-none focus:ring-2 focus:ring-emerald-600 bg-white shadow-2xs"
                  />
                  <span className="absolute right-3 top-2.5 text-[11px] font-bold text-emerald-900 font-urdu pointer-events-none">
                    1 کلو ریٹ:
                  </span>
                  <span className="absolute left-3 top-2.5 text-[10px] text-gray-500 font-urdu pointer-events-none">
                    روپے
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleApplyFourWeights}
                  disabled={!kgRateInput || Number(kgRateInput) <= 0}
                  className="px-4 py-2.5 rounded-xl bg-emerald-800 hover:bg-emerald-900 disabled:opacity-50 text-white text-xs font-bold font-urdu flex items-center justify-center gap-1.5 shadow-sm transition-all cursor-pointer"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  <span>تمام 7 اوزان خودکار بنائیں (1g، 10g، 50g، 100g، 250g، 500g، 1kg)</span>
                </button>
              </div>

              {/* Quick Add other weights */}
              <div className="flex flex-wrap items-center gap-2 pt-0.5 text-gray-700">
                <span className="text-[10px] font-bold font-urdu text-emerald-950">دیگر اوزان شامل کریں:</span>
                <button
                  type="button"
                  onClick={() => handleAddSpecificWeight(1, '1 گرام', '1g')}
                  disabled={!kgRateInput || Number(kgRateInput) <= 0}
                  className="px-2.5 py-1 bg-white hover:bg-emerald-100 disabled:opacity-40 text-emerald-900 border border-emerald-300 rounded-lg text-[10px] font-urdu font-bold shadow-2xs transition-colors cursor-pointer"
                >
                  + 1 گرام (20% اضافہ)
                </button>
                <button
                  type="button"
                  onClick={() => handleAddSpecificWeight(10, '10 گرام', '10g')}
                  disabled={!kgRateInput || Number(kgRateInput) <= 0}
                  className="px-2.5 py-1 bg-white hover:bg-emerald-100 disabled:opacity-40 text-emerald-900 border border-emerald-300 rounded-lg text-[10px] font-urdu font-bold shadow-2xs transition-colors cursor-pointer"
                >
                  + 10 گرام (20% اضافہ)
                </button>
                <button
                  type="button"
                  onClick={() => handleAddSpecificWeight(50, '50 گرام', '50g')}
                  disabled={!kgRateInput || Number(kgRateInput) <= 0}
                  className="px-2.5 py-1 bg-white hover:bg-emerald-100 disabled:opacity-40 text-emerald-900 border border-emerald-300 rounded-lg text-[10px] font-urdu font-bold shadow-2xs transition-colors cursor-pointer"
                >
                  + 50 گرام (20% اضافہ)
                </button>
                <button
                  type="button"
                  onClick={() => handleAddSpecificWeight(750, '750 گرام (تین پاؤ)', '750g')}
                  disabled={!kgRateInput || Number(kgRateInput) <= 0}
                  className="px-2.5 py-1 bg-white hover:bg-emerald-100 disabled:opacity-40 text-emerald-900 border border-emerald-300 rounded-lg text-[10px] font-urdu font-bold shadow-2xs transition-colors cursor-pointer"
                >
                  + 750 گرام (5% اضافہ)
                </button>
                <button
                  type="button"
                  onClick={() => handleAddSpecificWeight(1000, '1 کلو گرام (1 Kg)', '1 Kg')}
                  disabled={!kgRateInput || Number(kgRateInput) <= 0}
                  className="px-2.5 py-1 bg-white hover:bg-emerald-100 disabled:opacity-40 text-emerald-900 border border-emerald-300 rounded-lg text-[10px] font-urdu font-bold shadow-2xs transition-colors cursor-pointer"
                >
                  + 1 کلو گرام (بنیادی ریٹ)
                </button>
              </div>
            </div>

            {/* Custom weight add bar */}
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-bold text-gray-700 font-urdu">
                مقرر کردہ پیکنگ اوزان و قیمتیں ({formData.variants.length})
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleManualSort}
                  className="text-xs font-bold text-emerald-800 hover:text-emerald-950 font-urdu flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 px-2.5 py-1 rounded-lg border border-emerald-200 shadow-2xs cursor-pointer transition-colors"
                  title="اوزان کو کم سے زیادہ کی ترتیب دیں"
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <span>ترتیب دیں</span>
                </button>
                <button
                  type="button"
                  onClick={addVariant}
                  className="text-xs text-pansar-700 hover:text-pansar-900 font-bold font-urdu flex items-center gap-1 hover:underline cursor-pointer bg-pansar-50 px-2.5 py-1 rounded-lg border border-pansar-200 transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>نیا کسٹم وزن شامل کریں</span>
                </button>
              </div>
            </div>

            {/* Variants input table / grid */}
            <div className="space-y-2">
              {formData.variants.map((variant, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50/70 p-3 rounded-2xl border border-gray-200 shadow-2xs">
                  <div className="col-span-6 sm:col-span-6">
                    <label className="block text-[10px] text-gray-500 font-urdu mb-0.5">
                      وزن کا عنوان (اردو):
                    </label>
                    <input
                      type="text"
                      required
                      value={variant.weight}
                      onChange={(e) => handleVariantChange(idx, 'weight', e.target.value)}
                      placeholder="مثلاً: 100 گرام، کلو"
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-urdu font-semibold"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-4">
                    <label className="block text-[10px] text-gray-500 font-urdu mb-0.5">
                      قیمت (روپے):
                    </label>
                    <input
                      type="number"
                      required
                      value={variant.price}
                      onChange={(e) => handleVariantChange(idx, 'price', e.target.value)}
                      placeholder="قیمت روپے میں"
                      className="w-full p-2 bg-white rounded-lg border border-gray-200 text-xs font-sans font-bold text-right"
                    />
                  </div>
                  <div className="col-span-2 text-center pt-3">
                    <button
                      type="button"
                      onClick={() => removeVariant(idx)}
                      disabled={formData.variants.length <= 1}
                      className="text-gray-400 hover:text-rose-600 disabled:opacity-25 p-1 cursor-pointer transition-colors"
                      title="یہ وزن ڈیلیٹ کریں"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

          </div>

          {/* Card 5: Stock Status & Availability & Action Buttons */}
          <div className="bg-white p-5 sm:p-7 rounded-3xl border border-gray-200/80 shadow-xs space-y-6">
            <h4 className="font-bold text-sm sm:text-base text-gray-900 font-urdu border-b border-gray-100 pb-3 flex items-center justify-between">
              <span>سٹاک کی دستیابی اور نمائش کی ترتیبات (Stock & Visibility)</span>
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Option 1: In Stock */}
              <div
                onClick={() => setFormData({ ...formData, inStock: true })}
                className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-3.5 ${
                  formData.inStock
                    ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="stockStatus"
                  checked={formData.inStock === true}
                  onChange={() => setFormData({ ...formData, inStock: true })}
                  className="mt-1 text-emerald-600 focus:ring-emerald-500 w-4 h-4 cursor-pointer"
                />
                <div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className={`w-4 h-4 ${formData.inStock ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <span className="font-bold text-xs sm:text-sm text-gray-900 font-urdu">سٹاک میں دستیاب ہے (In Stock)</span>
                  </div>
                  <p className="text-[11px] text-gray-500 font-urdu mt-0.5">
                    یہ پروڈکٹ سٹور پر خریداروں کے لیے کھلی ہے اور کارٹ میں شامل کی جا سکتی ہے۔
                  </p>
                </div>
              </div>

              {/* Option 2: Out of Stock */}
              <div
                onClick={() => setFormData({ ...formData, inStock: false })}
                className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-start gap-3.5 ${
                  !formData.inStock
                    ? 'border-rose-500 bg-rose-50/50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="stockStatus"
                  checked={formData.inStock === false}
                  onChange={() => setFormData({ ...formData, inStock: false })}
                  className="mt-1 text-rose-600 focus:ring-rose-500 w-4 h-4 cursor-pointer"
                />
                <div>
                  <div className="flex items-center gap-1.5">
                    <XCircle className={`w-4 h-4 ${!formData.inStock ? 'text-rose-600' : 'text-gray-400'}`} />
                    <span className="font-bold text-xs sm:text-sm text-gray-900 font-urdu">آؤٹ آف سٹاک (Out of Stock)</span>
                  </div>
                  <p className="text-[11px] text-gray-500 font-urdu mt-0.5">
                    پروڈکٹ پر "آؤٹ آف اسٹاک" لکھا آئے گا اور اس کا آرڈر عارضی طور پر بند ہوگا۔
                  </p>
                </div>
              </div>
            </div>

            {/* Featured Product Option */}
            <div className="pt-2">
              <label className="flex items-center gap-3 p-3.5 rounded-2xl bg-amber-50/60 border border-amber-200/80 cursor-pointer text-xs sm:text-sm font-bold text-amber-950 font-urdu">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4 cursor-pointer"
                />
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span>نمایاں پروڈکٹ بنائیں (Featured / ہوم پیج پر نمایاں سلائیڈر میں دکھائیں)</span>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs sm:text-sm font-bold font-urdu cursor-pointer transition"
              >
                منسوخ کریں
              </button>

              <button
                type="submit"
                className="px-7 py-2.5 rounded-xl bg-pansar-800 hover:bg-pansar-900 text-white text-xs sm:text-sm font-bold font-urdu flex items-center gap-2 shadow transition cursor-pointer"
              >
                <Save className="w-4 h-4" />
                <span>محفوظ کریں (Save Product)</span>
              </button>
            </div>
          </div>

        </form>

      </div>
    </div>
  );
};

export default ProductFormModal;
